import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect } from 'react';
import { AttachMoney, Add, Save, Search, Clear } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { useImportesPersonal } from '../hooks/useImportesPersonal';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const TIPO_IMPORTE_LABELS = { 1: 'Importe 1', 2: 'Importe 2', 3: 'Importe 3' };
const FORM_INICIAL = { idPersona: '', idTipoEstudio: '', idCentro: '', tipoImporte: '1', esTecnico: true };

const formatPeso = (v) => v != null ? `$${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

const GrillaImportesPersonal = () => {
  const { importes, personal, tiposEstudios, centros, pagos, roles, loading, cargarFiltros, buscar, guardar, actualizar, eliminar } = useImportesPersonal();
  const [filtroRol, setFiltroRol] = useState('2');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroPersona, setFiltroPersona] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [toast, setToast] = useState(null);

  const esTecnicoActual = filtroRol === '2';

  useEffect(() => {
    cargarFiltros(true);
    buscar(null, null, true);
  }, []); // eslint-disable-line

  const getValorImporte = (idTipoEstudio, idCentro, tipoImporte) => {
    const pago = pagos.find(p => p.idTipoEstudio === parseInt(idTipoEstudio) && p.idCentro === parseInt(idCentro));
    if (!pago) return null;
    return formatPeso(pago[`importe${tipoImporte}`]);
  };

  const handleRolChange = (rolId) => {
    const esTecnico = rolId === '2';
    setFiltroRol(rolId);
    setFiltroPersona('');
    cargarFiltros(esTecnico);
    buscar(filtroTipo || null, null, esTecnico);
  };

  const handleBuscar = () => buscar(filtroTipo || null, filtroPersona || null, esTecnicoActual);
  const handleLimpiar = () => {
    setFiltroTipo('');
    setFiltroPersona('');
    buscar(null, null, esTecnicoActual);
  };

  const abrirNuevo = () => {
    setForm({ ...FORM_INICIAL, esTecnico: esTecnicoActual });
    setModal({ mode: 'nuevo' });
  };

  const abrirEditar = (item) => {
    setForm({
      idPersona: item.idMedico ?? '',
      idTipoEstudio: item.idTipoEstudio ?? '',
      idCentro: item.idCentro ?? '',
      tipoImporte: String(item.tipoImporte ?? '1'),
      esTecnico: item.esTecnico,
    });
    setModal({ mode: 'editar', item });
  };

  const esDuplicado = (idPersona, idTipoEstudio, idCentro, excludeId) =>
    importes.some(i =>
      i.idMedico === parseInt(idPersona) &&
      i.idTipoEstudio === parseInt(idTipoEstudio) &&
      i.idCentro === parseInt(idCentro) &&
      i.id !== excludeId
    );

  const handleGuardar = async (e) => {
    e.preventDefault();
    const excludeId = modal.mode === 'editar' ? modal.item.id : undefined;
    if (esDuplicado(form.idPersona, form.idTipoEstudio, form.idCentro, excludeId)) {
      setToast({ message: 'Ya existe un importe para esta combinación de persona, práctica y centro.', type: 'error' });
      return;
    }
    const body = {
      IdPersona: parseInt(form.idPersona),
      IdTipoEstudio: parseInt(form.idTipoEstudio),
      IdCentro: parseInt(form.idCentro),
      TipoImporte: parseInt(form.tipoImporte),
      EsTecnico: form.esTecnico,
    };
    try {
      if (modal.mode === 'editar') {
        await actualizar(modal.item.id, body);
        setToast({ message: 'Importe actualizado con éxito.', type: 'success' });
      } else {
        await guardar(body);
        setToast({ message: 'Importe guardado con éxito.', type: 'success' });
      }
      setModal(null);
      buscar(filtroTipo || null, filtroPersona || null, esTecnicoActual);
    } catch {
      setToast({ message: 'Error al guardar.', type: 'error' });
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminar(confirmEliminar.id, confirmEliminar.esTecnico);
      setToast({ message: 'Importe eliminado con éxito.', type: 'success' });
      buscar(filtroTipo || null, filtroPersona || null, esTecnicoActual);
    } catch {
      setToast({ message: 'Error al eliminar.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <AttachMoney sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Administrar Importes
          </h2>
          <span className="page-subtitle">Asignación de tipo de importe por práctica y personal</span>
        </div>

        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modal?.mode === 'editar' ? 'Editar Importe' : 'Nuevo Importe'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.75rem' }}>
              <div className="row g-3">
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Rol *</Form.Label>
                    <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.25rem' }}>
                      {[{ label: 'Técnico', value: true }, { label: 'Médico', value: false }].map(r => (
                        <Button
                          key={String(r.value)}
                          type="button"
                          size="sm"
                          variant={form.esTecnico === r.value ? 'primary' : 'outline-secondary'}
                          onClick={() => setForm(f => ({ ...f, esTecnico: r.value, idPersona: '' }))}
                          style={{ minWidth: '80px' }}
                        >
                          {r.label}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>{form.esTecnico ? 'Técnico' : 'Médico'} *</Form.Label>
                    <Form.Select value={form.idPersona} onChange={(e) => setForm(f => ({ ...f, idPersona: e.target.value }))} required>
                      <option value="">Seleccione...</option>
                      {personal
                        .filter(p => form.esTecnico ? true : true) // personal ya viene filtrado por rol del hook
                        .map(p => <option key={p.idMedico} value={p.idMedico}>{p.medico1}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Tipo de Práctica *</Form.Label>
                    <Form.Select value={form.idTipoEstudio} onChange={(e) => setForm(f => ({ ...f, idTipoEstudio: e.target.value }))} required>
                      <option value="">Seleccione...</option>
                      {tiposEstudios.map(t => <option key={t.idTipoEstudio} value={t.idTipoEstudio}>{t.tipoEstudio}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Centro *</Form.Label>
                    <Form.Select
                      value={form.idCentro}
                      onChange={(e) => setForm(f => ({ ...f, idCentro: e.target.value }))}
                      required
                      isInvalid={!!(form.idPersona && form.idTipoEstudio && form.idCentro && esDuplicado(form.idPersona, form.idTipoEstudio, form.idCentro, modal?.mode === 'editar' ? modal.item.id : undefined))}
                    >
                      <option value="">Seleccione...</option>
                      {centros.map(c => <option key={c.idCentro} value={c.idCentro}>{c.centro}</option>)}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Ya existe un importe para esta combinación.
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Tipo de Importe *</Form.Label>
                    <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.25rem' }}>
                      {[1, 2, 3].map(n => {
                        const val = getValorImporte(form.idTipoEstudio, form.idCentro, n);
                        const selected = form.tipoImporte === String(n);
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, tipoImporte: String(n) }))}
                            style={{
                              flex: 1,
                              padding: '0.5rem 0.75rem',
                              borderRadius: '0.5rem',
                              border: selected ? '2px solid #6d1212' : '2px solid #dee2e6',
                              background: selected ? '#6d1212' : '#fff',
                              color: selected ? '#fff' : '#1e293b',
                              cursor: 'pointer',
                              textAlign: 'center',
                              transition: 'all 0.15s',
                              boxShadow: selected ? '0 2px 8px rgba(109,18,18,0.2)' : 'none',
                            }}
                          >
                            <div style={{ fontWeight: 700, fontSize: '0.8rem' }}>{TIPO_IMPORTE_LABELS[n]}</div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 800, marginTop: '0.1rem', color: selected ? '#ffd700' : '#6d1212' }}>
                              {val ?? <span style={{ opacity: 0.4, fontSize: '0.8rem' }}>Sin valor</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </Form.Group>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !form.idPersona || !form.idTipoEstudio || !form.idCentro || esDuplicado(form.idPersona, form.idTipoEstudio, form.idCentro, modal?.mode === 'editar' ? modal.item.id : undefined)}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {modal?.mode === 'editar' ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtroRol ? 'filter-active' : ''}`}>
                <label className="filter-label">Rol</label>
                <select className="filter-input" value={filtroRol} onChange={(e) => handleRolChange(e.target.value)}>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.rol}</option>)}
                </select>
              </div>
              <div className={`filter-group ${filtroTipo ? 'filter-active' : ''}`}>
                <label className="filter-label">Tipo de Práctica</label>
                <select className="filter-input" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                  <option value="">Todos</option>
                  {tiposEstudios.map(t => <option key={t.idTipoEstudio} value={t.idTipoEstudio}>{t.tipoEstudio}</option>)}
                </select>
              </div>
              <div className={`filter-group ${filtroPersona ? 'filter-active' : ''}`}>
                <label className="filter-label">{esTecnicoActual ? 'Técnico' : 'Médico'}</label>
                <select className="filter-input" value={filtroPersona} onChange={(e) => setFiltroPersona(e.target.value)}>
                  <option value="">Todos</option>
                  {personal.map(p => <option key={p.idMedico} value={p.idMedico}>{p.medico1}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={handleBuscar} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={handleLimpiar}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{importes.length} registros</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={abrirNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th>{esTecnicoActual ? 'Técnico' : 'Médico'}</th>
                  <th>Tipo de Práctica</th>
                  <th>Centro</th>
                  <th style={{ textAlign: 'center' }}>Tipo de Importe</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {importes.length === 0 ? (
                  <tr><td colSpan={5} className="empty-state">{loading ? 'Cargando...' : 'No hay registros disponibles'}</td></tr>
                ) : importes.map((item) => (
                  <tr key={item.id}>
                    <td>{item.persona}</td>
                    <td>{item.tipoEstudio}</td>
                    <td>{item.centro}</td>
                    <td style={{ textAlign: 'center' }}>
                      {TIPO_IMPORTE_LABELS[item.tipoImporte] ?? item.tipoImporte}
                      {(() => { const v = getValorImporte(item.idTipoEstudio, item.idCentro, item.tipoImporte); return v ? <span style={{ color: '#6d1212', fontWeight: 600, marginLeft: '0.4rem' }}>({v})</span> : null; })()}
                    </td>
                    <td className="action-cell">
                      <RowActions onEdit={() => abrirEditar(item)} onDelete={() => setConfirmEliminar({ id: item.id, esTecnico: item.esTecnico, label: item.persona })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar Importe"
        message={`¿Eliminar el importe de "${confirmEliminar?.label}"?`}
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaImportesPersonal;
